import { useRef, useEffect } from 'react';
import type { StrokeInput, AnimatedDrawing } from '@twitch-draw/shared';
import { renderStroke } from '../canvas/renderer';

interface SubmissionPreviewProps {
  strokes: StrokeInput[];
  animation?: AnimatedDrawing;
  width?: number;
  height?: number;
}

export function SubmissionPreview({ strokes, animation, width = 120, height = 68 }: SubmissionPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const drawFrame = (frameStrokes: StrokeInput[]) => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.fillRect(0, 0, width, height);
      for (const stroke of frameStrokes) {
        renderStroke(ctx, stroke.points, stroke.color, stroke.width * 0.5, width, height);
      }
    };

    if (animation && animation.frames.length > 1) {
      let frameIdx = 0;
      let lastTime = 0;
      const frameInterval = 1000 / animation.fps;

      const animate = (time: number) => {
        if (time - lastTime >= frameInterval) {
          frameIdx = (frameIdx + 1) % animation.frames.length;
          drawFrame(animation.frames[frameIdx]);
          lastTime = time;
        }
        animRef.current = requestAnimationFrame(animate);
      };

      drawFrame(animation.frames[0]);
      animRef.current = requestAnimationFrame(animate);

      return () => cancelAnimationFrame(animRef.current);
    } else {
      drawFrame(strokes);
    }
  }, [strokes, animation, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="submission-preview"
    />
  );
}
